import { IoEvent, MediaProperty as MediaProp } from '../../peregrineFlowInterfaces';
import { Box, IconButton, TextField, Tooltip, Typography, LinearProgress } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { NodePropertyProp } from '../NodePropertyPane';
import { useCallback, useEffect, useRef, useState } from 'react';
import FileManager, { FileEntry } from '../../FileBrowser/FileManager';
import { SharedFile } from '../../FileBrowser/SharedFileBrowser';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import CloudUploadIcon from '@mui/icons-material/Cloud';
import { socket, URL } from '../../socket';
import useStore from '../../store';
import { enqueueSnackbar } from 'notistack';

function MediaProperty({ property, onChange }: NodePropertyProp) {
    const p = property as unknown as MediaProp;
    const [path, setPath] = useState<string | string[]>(p.value);
    const [changed, setChanged] = useState<boolean>(false);
    const [showFileManager, setShowFileManager] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<{ loaded: number, total: number, filename: string } | undefined>(undefined);
    const token = useStore((state) => state.token);

    const [isDragging, setIsDragging] = useState(false);
    const [rootPath, setRootPath] = useState<string | undefined>(undefined);
    const [pendingUpload, setPendingUpload] = useState<File | undefined>(undefined);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!changed) {
            return
        }

        if (path === undefined) {
            p.value = "";
            onChange?.(p)
            setChanged(false);
            return;
        }

        p.value = path;
        onChange?.(p)
        setChanged(false);
    }, [path, changed, p, onChange])

    // Initial check for root path and tmp folder
    useEffect(() => {
        const onIoEvent = (data: IoEvent) => {
            if (data.event_type === 'get_temporary_folder') {
                const tempFolder = data.data as FileEntry;
                // If parent is undefined, we are at the root
                setRootPath(tempFolder.path);
            }
        };

        socket.on('io', onIoEvent);
        socket.emit('io', { event_type: "get_temporary_folder" } as IoEvent);

        return () => {
            socket.off('io', onIoEvent);
        };
    }, []);


    const performUpload = useCallback((destination: FileEntry, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('destination', destination.path);
        console.log("Uploading to ", destination.path, file);

        if (token) {
            formData.append('token', token);
        } else {
            formData.append('temporary', token ? 'false' : 'true');
            formData.append('session_id', socket.id!);
        }


        const xhr = new XMLHttpRequest()
        xhr.upload.onload = () => {
            setUploadProgress(undefined);
        }

        xhr.onreadystatechange = () => {
            if (xhr.readyState !== 4) return;

            if (xhr.status >= 500) {
                console.error(xhr.responseText)
                enqueueSnackbar(xhr.responseText, { variant: "error" });
                return;
            }

            const json = JSON.parse(xhr.responseText);

            if (400 >= xhr.status && 500 < xhr.status) {
                console.error(json, xhr.status)
                enqueueSnackbar(xhr.responseText, { variant: "error" });
                return;
            }

            // Update path to the uploaded file
            setPath(`${destination.path}/${file.name}`);
            setChanged(true);
        }

        xhr.upload.onerror = (e) => {
            setUploadProgress(undefined);
            console.error("upload failed", e);
            enqueueSnackbar("Upload failed", { variant: "error" });
        }

        xhr.upload.onabort = () => {
            setUploadProgress(undefined);
        }

        xhr.upload.onprogress = (e) => {
            setUploadProgress({ loaded: e.loaded, total: e.total, filename: file.name });
        }

        xhr.open('POST', `${URL}/upload`)
        xhr.send(formData);
    }, [token]);

    // Handle pending upload once tmp exists
    useEffect(() => {
        if (pendingUpload && rootPath) {
            const tmpPathEntry = { path: `${rootPath}/tmp`, name: 'tmp', type: 'folder' } as FileEntry;
            performUpload(tmpPathEntry, pendingUpload);
            setPendingUpload(undefined);
        }
    }, [pendingUpload, performUpload, rootPath]);

    const onFileManagerClose = useCallback(() => {
        setShowFileManager(false);
    }, []);

    const onSelectFile = useCallback((entry: FileEntry | SharedFile) => {
        setChanged(true);
        setPath(entry.path);
        setShowFileManager(false);
    }, []);

    const handleUploadProcess = useCallback((file: File) => {
        if (!rootPath) return;
        const tmpPathEntry = { path: `${rootPath}`, name: 'tmp', type: 'folder' } as FileEntry;
        performUpload(tmpPathEntry, file);
    }, [rootPath, performUpload]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        if (!rootPath) return;

        const files = e.dataTransfer.files;
        if (files.length === 0) return;

        const file = files[0];
        handleUploadProcess(file);
    }, [rootPath, handleUploadProcess]);

    const handleBoxClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (!rootPath) return;
        const files = e.target.files;
        if (files && files.length > 0) {
             const file = files[0];
             handleUploadProcess(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [rootPath, handleUploadProcess]);

    return (
        <Box sx={{ width: '100%' }}>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
            <Box display="flex" alignItems="center">
                <Typography variant="caption">{p.label}</Typography>
                {p.description && (
                    <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{p.description}</div>} componentsProps={{ tooltip: { sx: { maxWidth: 'none' } } }}>
                        <HelpOutline fontSize="small" sx={{ ml: 1, color: 'text.secondary', cursor: 'help', width: 16, height: 16 }} />
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    value={path}
                    onChange={(e) => { setPath(e.target.value) }}
                    onBlur={() => setChanged(true)}
                    sx={{ textOverflow: 'ellipsis' }}
                    onKeyDownCapture={(e) => {
                        if (e.key !== "Enter") return;
                        setChanged(true);
                    }}
                    placeholder="Paste URL or select file"
                />

                <Tooltip title="Browse Files">
                    <IconButton type="button" sx={{ ml: 1, mb: 1 }} aria-label="search" onClick={() => setShowFileManager(true)}>
                        <OpenInBrowserIcon />
                    </IconButton>
                </Tooltip>

                <FileManager
                    open={showFileManager}
                    onClose={onFileManagerClose}
                    mode="select"
                    onSelectFile={onSelectFile}
                    title="Select File"
                />
            </Box>

            <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleBoxClick}
                sx={{
                    border: isDragging ? '2px dashed #1976d2' : '1px dashed grey',
                    borderRadius: 1,
                    p: 2,
                    mt: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragging ? 'action.hover' : 'background.paper',
                    transition: 'background-color 0.2s, border-color 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    height: 200
                }}
            >
                <CloudUploadIcon
                    sx={{
                        fontSize: 48,
                        color: isDragging ? 'primary.main' : 'text.secondary',
                        transition: 'color 0.2s',
                        marginTop: '32px'
                    }}
                />
                <Typography variant="body2" color="textSecondary">
                    Drag & Drop files here or click to upload temporary files.
                </Typography>
            </Box>

            {uploadProgress && (
                <Box sx={{ width: '100%', mt: 1 }}>
                    <LinearProgress variant="determinate" value={(uploadProgress.loaded / uploadProgress.total) * 100} />
                    <Typography variant="caption" color="text.secondary">Uploading {uploadProgress.filename}...</Typography>
                </Box>
            )}
        </Box>
    )
}

export default MediaProperty;
